import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Phone, ShieldCheck, Award, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = 'w-full px-4 py-3 border border-[#E5E7EB] rounded-md text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40';

const TIER_INFO = {
  partner: { label: 'Partner', accent: '#1A3A5F', badge: 'bg-[#1A3A5F]/10 text-[#1A3A5F]' },
  gold: { label: 'Gold Partner', accent: '#F5871F', badge: 'bg-[#F5871F]/10 text-[#F5871F]' },
  larger: { label: 'Larger teams', accent: '#1A3A5F', badge: 'bg-[#1A3A5F]/10 text-[#1A3A5F]' },
};

const BookACall = () => {
  const [sp] = useSearchParams();
  const tier = ['gold', 'partner', 'larger'].includes(sp.get('tier')) ? sp.get('tier') : null;
  const info = tier ? TIER_INFO[tier] : null;
  const staff = Number(sp.get('staff')) || null;
  const [who, setWho] = useState('');

  const [f, setF] = useState({ name: sp.get('name') || '', email: sp.get('email') || '', company: sp.get('company') || '', phone: '',
    message: staff ? `We have ${staff} staff and would like to talk about a package.` : '' });
  useEffect(() => {
    if (tier !== 'larger') return;
    axios.get(`${API}/public/sales-gate`).then((r) => { const n = r.data.contact_name; if (n && n !== 'our team') setWho(n); }).catch(() => {});
  }, [tier]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = 'Book a call | Care Tech Compare'; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error('Name and email are required'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/book-a-call`, {
        name: f.name.trim(), email: f.email.trim(),
        company: f.company.trim() || null, phone: f.phone.trim() || null,
        message: f.message.trim() || null, tier: tier || null, staff_count: staff,
      });
      setDone(true); toast.success(data.message || "Thanks! We'll be in touch.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="page-book-a-call">
      {/* Hero */}
      <section className="bg-[#1A3A5F]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          {info && (
            <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full mb-5 bg-white/10 text-white`} data-testid="book-tier-badge">
              {tier === 'gold' ? <Award className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />} {info.label}
            </span>
          )}
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight font-extrabold text-white leading-tight">
            {tier === 'larger' ? 'Book a meeting with Sales' : info ? `Become a ${info.label}` : 'Book a call with our team'}
          </h1>
          <p className="text-lg text-gray-300 mt-5 max-w-2xl mx-auto">
            {tier === 'larger'
              ? `Send us a meeting request and ${who || 'our Sales team'} will email you with the best times for a call about the right package for your team.`
              : info
              ? "Tell us a little about your company and we'll book a short call to scope the next steps."
              : "Whether you're a supplier or a buyer, tell us what you need and we'll set up a quick call."}
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-2 gap-10 items-start">
        {/* What Partner / Gold mean */}
        <div className="space-y-5" data-testid="book-tier-explainer">
          <h2 className="font-heading text-2xl font-bold text-gray-900">The verification tiers</h2>
          <div className={`border-2 rounded-xl p-6 bg-white ${tier === 'partner' ? 'border-[#1A3A5F]' : 'border-[#E5E7EB]'}`}>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1A3A5F] bg-[#1A3A5F]/10 px-3 py-1 rounded-full"><ShieldCheck className="w-3.5 h-3.5" /> Partner</span>
              <span className="text-sm font-semibold text-gray-500">Verified by CTC</span>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              A paid supplier whose company and product details have been <strong>reviewed and verified by the
              Care Tech Compare team</strong>. It signals to buyers that a real person has checked your listing is
              accurate and legitimate. We scope it on a short call, agree the onboarding, and put your Partner badge live.
            </p>
          </div>
          <div className={`border-2 rounded-xl p-6 bg-white ${tier === 'gold' ? 'border-[#F5871F]' : 'border-[#E5E7EB]'}`}>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#F5871F] bg-[#F5871F]/10 px-3 py-1 rounded-full"><Award className="w-3.5 h-3.5" /> Gold Partner</span>
              <span className="text-sm font-semibold text-gray-500">Independently assured by Care City</span>
            </div>
            <p className="text-sm text-gray-600 mt-3">
              The highest tier of trust. A Partner who has <strong>also been independently checked and assured by our
              delivery partner, Care City</strong> - an external, expert review of the product and the organisation behind it.
              <strong> Assurance is earned, never bought</strong>; the call lets us explain the process and what's involved.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-7 shadow-sm">
          {done ? (
            <div className="text-center py-8" data-testid="book-success">
              <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
              <p className="font-heading text-xl font-bold text-gray-900">Request received.</p>
              <p className="text-sm text-gray-600 mt-2">{tier === 'larger' ? `Thanks - ${who || 'our Sales team'} will email you shortly with some times for a call.` : 'Thanks - our team will be in touch shortly to book your call.'} We typically reply within one working day.</p>
              <Link to="/browse" className="inline-flex items-center gap-1.5 text-[#00857D] font-semibold text-sm mt-5" data-testid="book-browse-link">Explore the directory <ArrowRight className="w-4 h-4" /></Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl font-bold text-gray-900">{tier === 'larger' ? 'Request a meeting' : 'Request your call'}</h2>
              <p className="text-sm text-gray-500 mt-1 mb-5">We'll come back to you within one working day.</p>
              <form onSubmit={submit} className="space-y-3" data-testid="book-form">
                <input type="text" required placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={field} data-testid="book-name" />
                <input type="email" required placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={field} data-testid="book-email" />
                <input type="text" placeholder="Company name" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className={field} data-testid="book-company" />
                <input type="tel" placeholder="Phone number (so we can call you)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={field} data-testid="book-phone" />
                <textarea rows={3} placeholder="Anything you'd like us to know?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className={field} data-testid="book-message" />
                <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold px-6 py-3 rounded-md transition-colors disabled:opacity-60" data-testid="book-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />} {tier === 'larger' ? 'Send meeting request' : 'Book my call'}
                </button>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default BookACall;
